import { SessionChangedError } from '@/services/api/SessionChangedError'
import { StaleReplayError } from '@/services/api/StaleReplayError'

/**
 * Who a write belongs to, and when its idempotency key was first sent (unset
 * while it has never left the device).
 *
 * An idempotency key names one user's logical write, and the server only
 * remembers it for 24h after first seeing it (`Idempotent` concern). Every
 * keyed request goes through `request()`, which checks this record before each
 * attempt — a live send, a backoff retry, a replay after reload, a Retry from
 * the failed-changes sheet — so the key is never sent as anyone else
 * (`SessionChangedError`), nor re-sent once the server may have forgotten it
 * and would apply the write twice (`StaleReplayError`). Persisted with the
 * mutation (`lib/mutationReplay.ts`), so it survives a reload.
 *
 * `refused` marks a record whose latest attempt the server turned away before
 * running it (`refuseKeySend`), so nothing may treat that attempt as sent.
 */
export type KeySend = { userId: number | undefined; firstSentAt?: number; refused?: boolean }

// An hour of margin inside the server's 24h idempotency window.
const SEND_WINDOW_MS = 23 * 60 * 60 * 1000

const sends = new Map<string, KeySend>()

// Told when a refused write goes out again (see `onRefusedKeySent`).
let refusedKeySent: (() => void) | undefined

/**
 * Run `listener` as a write whose last attempt was refused is sent again,
 * before the request leaves. That attempt is a backoff retry with no cache
 * event before it to save the write as possibly sent; `main.tsx` saves here.
 */
export function onRefusedKeySent(listener: () => void): void {
  refusedKeySent = listener
}

/**
 * Check a keyed request may go out as `userId`, and record its first send.
 * Returns the record as it was before this attempt, for `refuseKeySend`.
 */
export function beginKeySend(key: string | undefined, userId: number | undefined): KeySend | undefined {
  if (!key) return undefined
  const send = sends.get(key)
  if (!send) {
    sends.set(key, { userId, firstSentAt: Date.now() })
    return undefined
  }
  assertMaySend(send, userId)
  sends.set(key, { userId, firstSentAt: send.firstSentAt ?? Date.now() })
  if (send.refused) refusedKeySent?.()
  return send
}

function assertMaySend(send: KeySend, userId: number | undefined): void {
  if (send.userId !== userId) throw new SessionChangedError()
  if (send.firstSentAt !== undefined && Date.now() - send.firstSentAt > SEND_WINDOW_MS) throw new StaleReplayError()
}

/**
 * This attempt can't have been applied: the server turned it away before
 * running the action (a 401 the session couldn't recover from, a 429 from the
 * rate limiter), or it never left the device (sent while offline): put back
 * the first send from before it, if any. Without this, a write queued behind
 * an unreachable refresh would be dropped as stale a day later although the
 * server never saw it.
 */
export function refuseKeySend(key: string | undefined, before: KeySend | undefined): void {
  const send = key ? sends.get(key) : undefined
  if (!key || !send) return
  sends.set(key, { userId: send.userId, firstSentAt: before?.firstSentAt, refused: true })
}

/** An attempt is starting: the refusal of the one before it no longer describes the write. */
export function clearKeyRefusal(key: string): void {
  const send = sends.get(key)
  if (send?.refused) sends.set(key, { userId: send.userId, firstSentAt: send.firstSentAt })
}

/** The server answered definitively (applied it, or never will): forget the key. */
export function settleKeySend(key: string | undefined): void {
  if (key) sends.delete(key)
}

export function getKeySend(key: string): KeySend | undefined {
  return sends.get(key)
}

/** Record a write's owner (on creation) or restore its record (on hydrate); never overwrites. */
export function seedKeySend(key: string, send: KeySend): void {
  if (!sends.has(key)) sends.set(key, send)
}
